import { useEffect, useState, type ReactNode } from 'react';
import { Nova, NovaMark } from '../engine/design';

// Shared visual shell for the auth + onboarding flow. Not a card on a gradient — a cinematic,
// unmistakably-Nova sign-in: a full-bleed stage that slowly cross-fades through real red-panda
// photography (Windows-spotlight style, with a caption + jump dots), a game-loading-screen tip
// rail where Nova hands you a rotating fact, and a frosted glass column that holds the form. The
// same shell wraps login AND every onboarding step, so the whole pre-app experience is one piece.

const REDUCE = typeof matchMedia !== 'undefined' && matchMedia('(prefers-reduced-motion: reduce)').matches;

// Real CC0 red-panda photography (public domain, no attribution required) in web/public. Each
// gets a spotlight caption in the Windows-lockscreen spirit.
const PHOTOS: { src: string; caption: string }[] = [
  { src: '/panda-hero.jpg', caption: 'On the lookout' },
  { src: '/panda-rain.jpg', caption: 'A quiet watch in the rain' },
  { src: '/panda-climb.jpg', caption: 'Down the branch, tail first' },
  { src: '/panda-forage.jpg', caption: 'Foraging at first light' },
  { src: '/panda-play.jpg', caption: 'Curious as ever' },
];

// Loading-screen tips: a mix of how NovaPSA actually works and red-panda trivia — the kind of
// thing a game drops while it loads. Nova "says" one at a time and they rotate.
const TIPS: string[] = [
  'Every invoice freezes when it is issued — corrections are credit notes, never edits.',
  'The ledger is hash-chained and append-only: tamper with one row and the whole chain breaks.',
  'Rates resolve for the work date, then snapshot — an agent’s cost and charge are effective-dated.',
  'Ticket time and project time run through one engine. There is exactly one place time is tracked.',
  'Press ⌘K anywhere in the app to jump to any record, screen, or action.',
  'Multi-tenant by default — Postgres row-level security walls off every tenant’s data.',
  'A quote converts straight into an invoice, a contract, or a project.',
  'Reply to a ticket by email — inbound mail threads back onto the conversation automatically.',
  'Red pandas have a “false thumb”: an extended wrist bone for gripping bamboo.',
  'A red panda’s ringed tail is nearly as long as its body — part balance beam, part winter blanket.',
];

export function AuthScene({ children }: { children: ReactNode }) {
  const [photo, setPhoto] = useState(0);
  const [tip, setTip] = useState(() => (REDUCE ? 0 : Math.floor(Math.random() * TIPS.length)));

  useEffect(() => {
    if (REDUCE) return;
    const a = setInterval(() => setPhoto((p) => (p + 1) % PHOTOS.length), 8000);
    const b = setInterval(() => setTip((t) => (t + 1) % TIPS.length), 6500);
    return () => { clearInterval(a); clearInterval(b); };
  }, []);

  return (
    <div className="auth-scene">
      <div className="auth-stage" aria-hidden="true">
        {PHOTOS.map((p, i) => (
          <div key={p.src} className={`auth-shot${i === photo ? ' is-on' : ''}`} style={{ backgroundImage: `url(${p.src})` }} />
        ))}
        <div className="auth-scrim" />
      </div>

      <div className="auth-stage-brand">
        <NovaMark size={30} animated />
        <span>Nova<b>PSA</b></span>
      </div>

      <div className="auth-overlay">
        <div className="auth-spotlight">
          <div className="auth-spot-cap">
            <span className="auth-spot-eyebrow">Meet Nova</span>
            <span className="auth-spot-title">{PHOTOS[photo]!.caption}</span>
          </div>
          <div className="auth-spot-dots" role="group" aria-label="Choose a photo">
            {PHOTOS.map((p, i) => (
              <button
                key={p.src}
                type="button"
                className={i === photo ? 'on' : ''}
                aria-label={p.caption}
                aria-pressed={i === photo}
                onClick={() => setPhoto(i)}
              />
            ))}
          </div>
        </div>

        <div className="auth-tip">
          <Nova variant="full" mood="think" size={44} animated ground />
          <div className="auth-tip-body">
            <span className="auth-tip-k">Did you know</span>
            <p key={tip} className="auth-tip-t">{TIPS[tip]}</p>
          </div>
        </div>
      </div>

      <div className="auth-panel">
        <div className="auth-panel-inner">{children}</div>
      </div>
    </div>
  );
}

export function AuthCard({
  title,
  subtitle,
  brand = true,
  children,
  footer,
}: {
  title?: ReactNode;
  subtitle?: ReactNode;
  brand?: boolean;
  children: ReactNode;
  footer?: ReactNode;
}) {
  return (
    <div className="auth-card">
      {brand && (
        <div className="auth-brand">
          <NovaMark size={24} />
          <span className="auth-word">Nova<b>PSA</b></span>
        </div>
      )}
      {(title || subtitle) && (
        <div className="auth-head">
          {title && <h1>{title}</h1>}
          {subtitle && <p>{subtitle}</p>}
        </div>
      )}
      <div className="auth-body">{children}</div>
      {footer && <div className="auth-foot">{footer}</div>}
    </div>
  );
}

// Onboarding progress. current: 0=Account 1=Organization 2=Verify 3=Set up.
const STEPS = ['Account', 'Organization', 'Verify', 'Set up'];
export function Stepper({ current }: { current: number }) {
  return (
    <div className="stepper" aria-label={`Step ${current + 1} of ${STEPS.length}`}>
      {STEPS.map((label, i) => (
        <div key={label} style={{ display: 'contents' }}>
          <div className={`step ${i < current ? 'done' : ''} ${i === current ? 'current' : ''}`}>
            <span className="dot">{i < current ? '✓' : i + 1}</span>
            <span className="label">{label}</span>
          </div>
          {i < STEPS.length - 1 && <span className={`bar ${i < current ? 'filled' : ''}`} />}
        </div>
      ))}
    </div>
  );
}

export function CheckIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 6L9 17l-5-5" />
    </svg>
  );
}

// Brand marks for the OAuth providers (inline so nothing is fetched externally).
export function ProviderMark({ id }: { id: string }) {
  if (id === 'google') {
    return (
      <svg viewBox="0 0 24 24" aria-hidden>
        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" />
        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z" />
        <path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z" />
        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1A11 11 0 0 0 2.18 7.06l3.66 2.84C6.71 7.3 9.14 5.38 12 5.38z" />
      </svg>
    );
  }
  if (id === 'azure' || id === 'microsoft') {
    return (
      <svg viewBox="0 0 24 24" aria-hidden>
        <path fill="#F25022" d="M2 2h9.5v9.5H2z" />
        <path fill="#7FBA00" d="M12.5 2H22v9.5h-9.5z" />
        <path fill="#00A4EF" d="M2 12.5h9.5V22H2z" />
        <path fill="#FFB900" d="M12.5 12.5H22V22h-9.5z" />
      </svg>
    );
  }
  return null;
}

// A small "Signed in as … · Sign out" line for onboarding cards.
export function SignedInLine({ email, onSignOut }: { email?: string; onSignOut: () => void }) {
  return (
    <>
      {email ? <>Signed in as {email} · </> : null}
      <button type="button" className="linklike" onClick={onSignOut}>
        Sign out
      </button>
    </>
  );
}
